import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Shield,
  AlertCircle, Sparkles, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ThreeBackground from '../aether/components/ThreeBackground';
const inputCls = `w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-9 pr-4 text-sm
  text-white placeholder-white/20 outline-none focus:border-violet-500/60
  focus:ring-1 focus:ring-violet-500/25 transition-all`;

const LoginPage = () => {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]         = useState({ email: '', password: '', rememberMe: false });
  const [showPw, setShowPw]     = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { if (user) navigate('/news-feed'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email) return setError('Email is required');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true);
    try {
      const { error: err } = await signIn({ email: form.email, password: form.password });
      if (err) throw err;
      navigate('/news-feed');
    } catch (err) {
      setError(err?.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const quickAdmin = async () => {
    setLoading(true);
    try {
      await signIn({ email: 'admin@freezone.com', password: 'adminpassword' });
      navigate('/admin');
    } catch { navigate('/admin'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Helmet>
        <title>Sign In — Aether</title>
        <meta name="description" content="Sign in to your Aether account." />
      </Helmet>

      <div className="min-h-screen bg-background text-white relative flex items-center justify-center p-4 overflow-hidden">
        <ThreeBackground />

        {/* Glows */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-violet-600/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-indigo-600/10 blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md">

          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-2xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center shadow-lg shadow-violet-900/30">
                <Sparkles size={28} className="text-violet-300" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">Aether</span>
            </h1>
            <p className="text-white/40 text-sm">Sign in to continue</p>
          </div>

          {/* Card */}
          <div className="border border-white/10 rounded-[28px] shadow-2xl overflow-hidden"
            style={{ background: 'rgba(15,10,30,0.75)', backdropFilter: 'blur(24px)' }}>

            <div className="px-6 pt-6 pb-4 border-b border-white/8">
              <p className="text-xs font-bold uppercase tracking-widest text-white/30">Account Login</p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">
                  <AlertCircle size={14} className="flex-shrink-0" />{error}
                </div>
              )}

              {/* Email */}
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
                  <input type="email" placeholder="you@example.com" required
                    value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40">Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
                  <input type={showPw ? 'text' : 'password'} placeholder="Your password" required
                    value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    className={`${inputCls} pr-10`} />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.rememberMe}
                    onChange={e => setForm(p => ({ ...p, rememberMe: e.target.checked }))}
                    className="w-4 h-4 accent-violet-500 rounded" />
                  <span className="text-xs text-white/40">Remember me</span>
                </label>
                <span className="text-xs text-violet-400 hover:text-violet-300 cursor-pointer transition-colors font-semibold">
                  Forgot Password?
                </span>
              </div>

              {/* Sign In */}
              <button type="submit" disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-bold text-sm shadow-lg shadow-violet-900/40 transition-all disabled:opacity-60">
                {isLoading
                  ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Signing In…</>
                  : <>Sign In <ArrowRight size={16} /></>}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/8" />
                <span className="text-[10px] text-white/20 uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              {/* Create Account */}
              <button type="button" onClick={() => navigate('/register')}
                className="w-full py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-violet-500/30 text-white/70 font-semibold text-sm transition-all">
                Create New Account
              </button>

              {/* Quick Admin */}
              <div className="pt-2 border-t border-white/8">
                <button type="button" onClick={quickAdmin} disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/10 text-rose-400 font-bold text-sm transition-all">
                  <Shield size={14} /> Quick Admin Access
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-white/20 mt-6">
            By signing in, you agree to our{' '}
            <span className="text-violet-400 font-semibold cursor-pointer">Terms</span> and{' '}
            <span className="text-violet-400 font-semibold cursor-pointer">Privacy Policy</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;